import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Iter-187 — Global Invite Cart.
 *
 * Admins can add talent to a cart from any surface (Discover, Profile
 * Modal, Applicant Drawer, User Detail, Favorites, Previous Workers,
 * Shift Detail talent search). The cart persists across page navigation
 * (in-memory state, intentionally not localStorage so it doesn't survive
 * a hard refresh and become stale).
 *
 * Iter-193 — When the cart is opened from a Shift Detail page we now
 * pre-bind the active shift so the picker defaults to the *live* shift
 * instead of "the first row from /open-next-14-days" (which used to
 * surface fake Miami Administrative-Assistant seeds).
 *
 * Each entry: { user_id, name, photo_url, role, city, state }
 */
const InviteCartContext = createContext({
  items: [],
  add: () => {},
  remove: () => {},
  clear: () => {},
  has: () => false,
  count: 0,
  openReview: () => {},
  closeReview: () => {},
  isReviewOpen: false,
  preselectedShift: null,
});

export const InviteCartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isReviewOpen, setReviewOpen] = useState(false);
  // Iter-193 — optional shift context the cart drawer should preselect.
  // Shape: { id, event_title, role_name, shift_date, start_time, city, state, market_id, market_metro_name, market_slug, talent_needed, spots_remaining }
  const [preselectedShift, setPreselectedShift] = useState(null);
  // Iter-193 — ambient context shift bound by a host page (e.g.
  // AdminShiftDetail). Used as the fallback preselection when the cart
  // is opened from a global UI (Floating Bar) so the picker can still
  // default to the *live* shift the admin is currently viewing.
  const [contextShift, setContextShift] = useState(null);

  const add = useCallback((talent) => {
    if (!talent || !talent.user_id) return;
    setItems((prev) => {
      if (prev.some((p) => String(p.user_id) === String(talent.user_id))) return prev;
      const entry = {
        user_id: String(talent.user_id),
        name: talent.name || talent.display_name || talent.first_name || talent.email || 'Talent',
        photo_url: talent.photo_url || talent.profile_photo_url || null,
        role: talent.role || talent.role_name || null,
        city: talent.city || null,
        state: talent.state || null,
        // Iter-191.1 — carry markets through to the invite preview chips.
        markets: Array.isArray(talent.markets) ? talent.markets : [],
        primary_market: talent.primary_market || null,
      };
      return [...prev, entry];
    });
  }, []);

  const remove = useCallback((userId) => {
    setItems((prev) => prev.filter((p) => String(p.user_id) !== String(userId)));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const has = useCallback(
    (userId) => items.some((p) => String(p.user_id) === String(userId)),
    [items],
  );

  // Iter-193 — openReview accepts an optional shift to pre-bind. Callers
  // passing the live shift (e.g. AdminShiftDetail) get inheritance for
  // free without prop-drilling. When omitted, we fall back to the
  // ambient `contextShift` (bound by a host page via bindContextShift).
  const openReview = useCallback((shift) => {
    const next = (shift && shift.id) ? shift : contextShift;
    setPreselectedShift(next && next.id ? next : null);
    setReviewOpen(true);
  }, [contextShift]);
  const closeReview = useCallback(() => {
    setReviewOpen(false);
    // Clear preselection so the next open from a non-shift surface
    // doesn't reuse stale context.
    setPreselectedShift(null);
  }, []);

  // Iter-193 — host page can bind/unbind an ambient shift context so the
  // floating Invite Cart bar (which has no shift state of its own) still
  // preselects the live shift the admin is looking at.
  const bindContextShift = useCallback((shift) => {
    setContextShift(shift && shift.id ? shift : null);
  }, []);
  const unbindContextShift = useCallback(() => setContextShift(null), []);

  const value = useMemo(
    () => ({
      items,
      add,
      remove,
      clear,
      has,
      count: items.length,
      openReview,
      closeReview,
      isReviewOpen,
      preselectedShift,
      bindContextShift,
      unbindContextShift,
    }),
    [items, add, remove, clear, has, openReview, closeReview, isReviewOpen, preselectedShift, bindContextShift, unbindContextShift],
  );

  return <InviteCartContext.Provider value={value}>{children}</InviteCartContext.Provider>;
};

export const useInviteCart = () => useContext(InviteCartContext);

export default InviteCartProvider;
